/**
 * AffiliatePitch — post-signup thank-you page.
 * Route: /pitch/:leadId
 *
 * Selected via campaign.post_signup_page === "affiliate_pitch".
 * A single long-scroll page with 8 sections that each terminate in a CTA
 * button pointing to `lead.affiliate_url` (with UTMs and lead_id appended).
 * Copy is verbatim from the user-provided DOCX (13.07.26 pitch spec).
 *
 * Design language matches the default landing page:
 *   background:  #fafaf9 (warm white)
 *   primary:     brand-amber (#d97706)
 *   text:        brand-charcoal (#292524), stone-500/600/700 muted
 *   cards:       bg-white rounded-3xl shadow-[0_12px_40px_rgba(28,25,23,0.08)]
 *                border border-black/5
 *   CTA:         rounded-full h-14 bg-brand-amber pulse-cta
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertTriangle, Check, ChevronDown, ArrowLeft, MessageCircle, Sparkles,
  Home as HomeIcon, X as XIcon, Clock,
} from "lucide-react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { BRAND_LOGO } from "@/lib/assets";

const PRIMARY_CTA = "אני רוצה לבדוק התאמה עכשיו (לוקח 60 שניות)";
const SECONDARY_CTA = "הבנתי. אני מוכן לבדוק התאמה";

export default function AffiliatePitch() {
  const { leadId } = useParams();
  const [lead, setLead] = useState(null);
  const [error, setError] = useState(null);
  const [openFaq, setOpenFaq] = useState(-1);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/leads/${leadId}/public`);
        setLead(data);
        track("funnel_view", {
          campaignId: data.campaign_id,
          leadId: data.lead_id,
          payload: { step: "pitch" },
        });
      } catch (_) {
        setError("הקישור אינו תקין");
      }
    })();
  }, [leadId]);

  const affiliateHref = lead?.affiliate_url || "#";
  const handleCtaClick = (source) => () => {
    if (!lead) return;
    track("funnel_affiliate_click", {
      campaignId: lead.campaign_id,
      leadId: lead.lead_id,
      payload: { source: `pitch_${source}` },
    });
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafaf9] p-6" dir="rtl">
        <div className="bg-white rounded-3xl p-8 text-center max-w-sm border border-black/5">
          <XIcon className="w-12 h-12 mx-auto text-rose-500 mb-3" />
          <h2 className="text-xl font-bold mb-2">קישור לא תקין</h2>
          <p className="text-stone-500 text-sm">חזרו לדף הבית ונסו שוב.</p>
          <Link to="/" className="mt-4 inline-block text-brand-amber font-semibold hover:underline">
            חזרה לדף הבית
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div dir="rtl" className="min-h-screen bg-[#fafaf9] text-brand-charcoal" data-testid="pitch-page">
      {/* Sticky header — matches LandingPage */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-black/5">
        <div className="max-w-4xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-4">
          <img src={BRAND_LOGO} alt="אמת או חובות" className="h-10 w-auto" data-testid="brand-logo" />
          {lead?.first_name ? (
            <span className="text-sm text-stone-500 hidden sm:inline">
              {lead.first_name}, המדריך בדרכו למייל
            </span>
          ) : null}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-8 py-8 md:py-12">
        {/* SECTION 1: Hero / Opening */}
        <PitchHero name={lead?.first_name} />

        {/* SECTION 2: The Problem */}
        <SectionCard testId="section-problem" delay={0.05}>
          <SubHeading amber>אבל הוא לא יציל אתכם לבד. הנה למה:</SubHeading>
          <Paragraph>
            אתם כנראה נמצאים במלכודת הכי מתסכלת של מעמד הביניים הישראלי. על הנייר? אתם סיפור הצלחה. עובדים קשה, מרוויחים יפה, אפילו הצלחתם לקנות דירה (שזה חתיכת הישג היום). כולם מסביב בטוחים שאתם מסודרים.
          </Paragraph>
          <Paragraph>
            אבל בפנים? כשאף אחד לא רואה? ב-10 לחודש אתם חוטפים התקף חרדה קטן. אתם מסתכלים על החשבון ורואים איך הכסף שקרעתם את עצמכם בשבילו פשוט נשאב:
          </Paragraph>
          <ExpenseList
            items={[
              "3,000 ש״ח להלוואה על הרכב...",
              "1,500 ש״ח להלוואה שלקחתם כדי לממן את החופשה מהקיץ שעבר...",
              "עוד 1,000 ש״ח על הסלון החדש שרכשתם לפני שנה…",
              "וזה עוד לפני שדיברנו על המשכנתה, הגנים והסופר.",
            ]}
          />
          <Paragraph>
            {`אתם מוצאים את עצמכם מתביישים להגיד שאין לכם אוויר, כי "היי, שתי משכורות הייטק/קבע/עצמאי, איך זה שאתם נחנקים?". אתם מרגישים שמשהו דפוק בכם. שאתם פשוט "גרועים בכסף".`}
          </Paragraph>

          <SubHeading amber className="mt-8">אבל אתם לא טועים. ולא דפוקים.</SubHeading>
          <Paragraph>
            הבנק פשוט עשה עליכם סיבוב. הבנקים מתים על הלוואות קטנות ובינוניות. הן פרות החלב שלהם. הם מחלקים אותן בלחיצת כפתור באפליקציה עם ריביות רצחניות, כי הם יודעים שאתם בלחץ של זמן. הם רוצים שתהיו מפוזרים, שתשלמו קצת פה וקצת שם, העיקר שלא תראו את התמונה המלאה. וככה, למרות שיש לכם את אחד הנכסים הכי יציבים בעולם (הדירה שלכם) – אתם חיים כמו סטודנטים שתפרנים.
          </Paragraph>
          <CtaButton href={affiliateHref} onClick={handleCtaClick("problem")} testId="pitch-cta-problem" />
        </SectionCard>

        {/* SECTION 3: The Solution */}
        <SectionCard testId="section-solution" delay={0.05} spotlight>
          <SubHeading amber>{`יש "דלת אחורית" למערכת הזו. והגיע הזמן שתכירו אותה.`}</SubHeading>
          <Paragraph>
            {`אנשים שבאמת מבינים בכסף לא משלמים 5 הלוואות שונות ב-5 תאריכים שונים עם 5 ריביות שונות שיורדות להם מהעו״ש. הם משתמשים בנכס שכבר יש להם כדי "לבלוע" את כל החובות הקטנים האלה והיקרים, להעלים אותם, ולהחזיר את הכל תחת קורת גג אחת, בריבית נורמלית ובתשלום חודשי אחד קטן בהרבה.`}
          </Paragraph>

          <div className="mt-6 rounded-2xl bg-amber-50/70 border border-amber-200/60 px-5 py-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-brand-amber" />
              <span className="text-xs font-bold text-brand-amber tracking-widest uppercase">בשפה המקצועית</span>
            </div>
            <SubHeading className="!mt-0 !mb-2">{`המהלך הכירורגי הזה, שנקרא "איחוד הלוואות תחת המשכנתה"`}</SubHeading>
            <Paragraph className="!text-stone-700">
              הוא הנשק הכי חזק של מי שיודע לשחק את המשחק. הוא יכול לחתוך לכם ברגע בין <strong className="text-brand-amber">2,000 ל-7,500 ש״ח</strong> כל חודש מההוצאות. כסף חי, מזומן, שפשוט נשאר אצלכם בעו״ש למכולת, לחופשה, או פשוט לשקט הנפשי שלכם.
            </Paragraph>
          </div>
          <CtaButton href={affiliateHref} onClick={handleCtaClick("solution")} testId="pitch-cta-solution" />
        </SectionCard>

        {/* SECTION 4: Disclaimer */}
        <SectionCard testId="section-disclaimer" delay={0.05}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-2xl bg-amber-50 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-brand-amber" strokeWidth={2.5} />
            </div>
            <SubHeading className="!mt-0 !mb-0">אזהרה: קחו צעד אחורה. זה לא קסם, וזה בטח לא מתאים לכולם.</SubHeading>
          </div>
          <Paragraph className="mt-4">
            לפני שאתם מתלהבים וחושבים שמצאתם כפתור קסם שמוחק חובות, בואו נצנן קצת:
          </Paragraph>
          <ul className="mt-4 space-y-3">
            {[
              "אם אין לכם נכס בבעלותכם — זה לא רלוונטי לכם. צאו מהעמוד, קראו את המדריך, ותתחילו לאזן את התקציב.",
              'אם אתם מחפשים "כסף קל" או הוקוס פוקוס להתעשרות — זה לא פה. מדובר פה על מהלך פיננסי, מתמטי, קר ומחושב שדורש בחינה מקיפה של הנתונים שלכם.',
              "לא לכל אחד אפשר לעזור. יש מצבים שיחס ההחזר כבר גבוה מדי, או שהנכס לא שווה מספיק, ואי אפשר לבצע את המהלך.",
              "אנחנו מקבלים פניות מעשרות משפחות כל שבוע, ואני אגיד לכם בכנות — לפחות חצי מהם אנחנו דוחים. הלו״ז של המומחים שלנו עמוס, ואנחנו עובדים רק עם מי שאנחנו בוודאות יכולים לחלץ לו סכום משמעותי מהחזרים.",
            ].map((t, i) => (
              <li key={i} className="flex gap-3 items-start bg-[#fafaf9] rounded-2xl px-4 py-3 border border-black/5">
                <span className="w-6 h-6 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <XIcon className="w-3.5 h-3.5 text-rose-600" strokeWidth={3} />
                </span>
                <span className="text-stone-700 leading-[1.65]">{t}</span>
              </li>
            ))}
          </ul>
          <CtaButton href={affiliateHref} onClick={handleCtaClick("disclaimer")} testId="pitch-cta-disclaimer" />
        </SectionCard>

        {/* SECTION 5: The Questionnaire step */}
        <SectionCard testId="section-questionnaire" delay={0.05}>
          <SubHeading amber>אז מה עכשיו? המבחן שלכם.</SubHeading>
          <Paragraph>
            בדיוק בגלל זה יצרנו את &quot;רנטגן התזרים&quot; — שאלון אבחון קצר של 60 שניות. השאלון הזה נועד לדבר אחד: לבדוק אם אתם בכלל קורצנו מהחומר (ויש לכם את הנתונים) שמאפשר לנו לעזור לכם להחזיר את האוויר לעו״ש.
          </Paragraph>

          <SubHeading className="mt-6">מה יוצא לכם מזה?</SubHeading>
          <Paragraph>
            מי שעובר את השאלון ונמצא מתאים, יועבר ישירות לתיאום שיחת אסטרטגיה אישית (בשווי אמיתי של <strong className="text-brand-amber">1,500 ש״ח</strong>) איתי או עם אחד מהמומחים הבכירים בצוות. בשיחה הזו אנחנו ננתח את הנתונים, נראה לכם בדיוק איפה הכסף שלכם מדמם, ונגיד לכם שחור על גבי לבן כמה כסף אנחנו יכולים לפנות לכם כל חודש.
          </Paragraph>
          <div className="mt-4 flex items-center gap-2 text-sm text-stone-500">
            <Check className="w-4 h-4 text-emerald-600" strokeWidth={3} />
            <span>בלי התחייבות, בלי לשלם לנו שקל על השיחה הזו.</span>
          </div>
          <CtaButton href={affiliateHref} onClick={handleCtaClick("questionnaire")} testId="pitch-cta-questionnaire" />
        </SectionCard>

        {/* SECTION 6: Cost of Inaction */}
        <SectionCard testId="section-inaction" delay={0.05}>
          <SubHeading className="text-rose-700">העלות של לא לעשות כלום:</SubHeading>
          <Paragraph>
            אתם יכולים לסגור את העמוד הזה עכשיו. באמת, הכל טוב. אבל בחודש הבא, כשהוויזה תרד, וכשההלוואה על הרכב תרד, והמשכנתה תרד... אתם תרגישו שוב את אותה מועקה בדיוק. תמשיכו לתרום לבנק את הכסף שהיה יכול ללכת לעתיד של הילדים שלכם.
          </Paragraph>
          <div className="mt-6 rounded-2xl bg-gradient-to-tr from-amber-50 to-white border-2 border-brand-amber/40 px-5 py-4">
            <SubHeading amber className="!mt-0 !mb-2">או —</SubHeading>
            <Paragraph className="!text-stone-800">
              שאתם יכולים להשקיע 60 שניות עכשיו, למלא את השאלון, ואולי לשנות את כל המסלול הכלכלי של המשפחה שלכם. <strong>הבחירה שלכם.</strong>
            </Paragraph>
          </div>
          <CtaButton href={affiliateHref} onClick={handleCtaClick("inaction")} testId="pitch-cta-inaction" />
        </SectionCard>

        {/* SECTION 7: Testimonials */}
        <SectionCard testId="section-proof" delay={0.05}>
          <SubHeading amber>&laquo; קצת הוכחות לפני שמתחילים?</SubHeading>
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <WhatsappCard
              name="ל***ה מ."
              text="תקשיב, ירדו לנו עכשיו 3,200 ש״ח מההחזרים. אני בוכה פה, אשכרה סגרנו חופשה ראשונה מאז הקורונה"
              testId="proof-card-1"
            />
            <WhatsappCard
              name="י***י כ."
              text="הייתי בטוח שהבנק יצחק עליי. פתאום כל ההלוואות נעלמו ונשאר רק תשלום אחד שפוי. אתם קוסמים"
              testId="proof-card-2"
            />
          </div>

          <p className="mt-8 mb-4 text-sm md:text-base font-[700] text-brand-charcoal text-center">
            ועדויות שהוקלטו על ידי לקוחות אמיתיים:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {SHORTS_TESTIMONIALS.map((id, i) => (
              <ShortsCard key={id} videoId={id} testId={`proof-short-${i + 1}`} />
            ))}
          </div>
          <CtaButton href={affiliateHref} onClick={handleCtaClick("proof")} testId="pitch-cta-proof" />
        </SectionCard>

        {/* SECTION 8: FAQ */}
        <SectionCard testId="section-faq" delay={0.05}>
          <SubHeading>שאלות נפוצות שאנחנו מקבלים (קראו לפני שאתם ממלאים את השאלון!):</SubHeading>
          <div className="mt-6 space-y-3">
            {FAQ_ITEMS.map((item, i) => {
              const open = openFaq === i;
              return (
                <div
                  key={i}
                  className={`bg-white rounded-2xl border overflow-hidden transition-colors ${
                    open ? "border-brand-amber shadow-[0_8px_28px_rgba(217,119,6,0.08)]" : "border-black/5"
                  }`}
                  data-testid={`pitch-faq-${i}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    className="w-full text-right px-5 md:px-6 py-4 md:py-5 flex items-center justify-between gap-4 hover:bg-stone-50 transition-colors"
                  >
                    <span className="text-base md:text-lg font-[700] text-brand-charcoal leading-snug">{item.q}</span>
                    <ChevronDown
                      className={`w-5 h-5 flex-shrink-0 transition-transform text-brand-amber ${open ? "rotate-180" : ""}`}
                      strokeWidth={2.5}
                    />
                  </button>
                  <motion.div
                    initial={false}
                    animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <p className="px-5 md:px-6 pb-5 md:pb-6 text-stone-700 leading-[1.75]">{item.a}</p>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </SectionCard>

        {/* SECTION 9: Final CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="mt-10 bg-gradient-to-tr from-brand-amber to-brand-amberHover text-white rounded-3xl p-6 md:p-10 relative overflow-hidden"
          data-testid="section-final-cta"
        >
          <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3 text-white/90">
              <Clock className="w-5 h-5" />
              <span className="text-sm font-bold tracking-wider">60 שניות מפרידות ביניכם לבין תשובה חד-משמעית</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-[800] mb-3 leading-tight">מוכנים לגלות אם המהלך מתאים לכם?</h3>
            <p className="text-white/90 leading-[1.7] mb-6 max-w-xl">
              השאלון קצר, דיסקרטי, ולא דורש שום מסמך. בסיום אנחנו נדע — ואתם תדעו — האם יש לנו על מה לדבר.
            </p>
            <a
              href={affiliateHref}
              target="_blank"
              rel="noreferrer"
              data-testid="pitch-cta-final"
              onClick={handleCtaClick("final")}
              className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-white text-brand-amber font-[800] hover:bg-stone-50 transition active:scale-95 pulse-cta"
            >
              {SECONDARY_CTA}
              <ArrowLeft className="w-5 h-5" />
            </a>
          </div>
        </motion.div>

        {/* Footer note */}
        <div className="mt-10 text-center text-xs text-stone-500 leading-relaxed pb-8">
          <p>* השאלון דיסקרטי לחלוטין. הנתונים לא משותפים עם צד שלישי ללא הסכמתכם.</p>
        </div>
      </main>
    </div>
  );
}

/* ==========================================================================
 * Small helpers — inline so the file is self-contained.
 * ========================================================================== */

function PitchHero({ name }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="relative mb-6"
      data-testid="section-hero"
    >
      <div className="absolute inset-0 amber-halo opacity-70 pointer-events-none -z-10" />
      <div className="text-center space-y-4 pt-4">
        <span className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-800 border border-emerald-200/70 px-3 py-1.5 rounded-full text-xs font-bold">
          <Check className="w-4 h-4" strokeWidth={3} />
          המדריך נשלח למייל!
        </span>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-[800] leading-[1.15] tracking-tight text-brand-charcoal">
          {name ? `${name}, ` : ""}
          <span className="text-brand-amber">רגע לפני שאתם סוגרים את העמוד...</span>
        </h1>
        <p className="text-lg md:text-xl text-stone-600 leading-[1.6] max-w-2xl mx-auto">
          יש פיל ענק בחדר שאנחנו חייבים לדבר עליו.
        </p>
      </div>

      <div className="mt-8 bg-white rounded-3xl shadow-[0_12px_40px_rgba(28,25,23,0.08)] border border-black/5 p-6 md:p-8">
        <p className="text-base md:text-lg text-stone-700 leading-[1.75]">
          המדריך (והגישה לקהילה) יגיעו לתיבת המייל שלכם תוך 3-4 דקות.
          {" "}
          <span className="text-stone-500">(אם אתם לא רואים אותו, תבדקו בתיקיית קידומי מכירות או ספאם).</span>
        </p>
        <p className="text-base md:text-lg text-stone-700 leading-[1.75] mt-3">
          אבל <strong className="text-brand-amber">רגע לפני שאתם הולכים לקרוא אותו, תעצרו הכל.</strong>
          {" "}
          כי אני הולך להגיד לכם משהו שאף יועץ פיננסי מעונב מהבנק לא יגיד לכם.
        </p>
      </div>
    </motion.section>
  );
}

function SectionCard({ children, testId, delay = 0, spotlight = false }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, delay }}
      className={`mt-6 bg-white rounded-3xl shadow-[0_12px_40px_rgba(28,25,23,0.08)] border p-6 md:p-8 ${
        spotlight ? "border-amber-200/70" : "border-black/5"
      }`}
      data-testid={testId}
    >
      {children}
    </motion.section>
  );
}

function SubHeading({ children, className = "", amber = false }) {
  return (
    <h2
      className={`text-xl md:text-2xl font-[800] leading-[1.3] mt-2 mb-3 ${
        amber ? "text-brand-amber" : "text-brand-charcoal"
      } ${className}`}
    >
      {children}
    </h2>
  );
}

function Paragraph({ children, className = "" }) {
  return (
    <p className={`text-base md:text-lg text-stone-700 leading-[1.75] ${className}`}>
      {children}
    </p>
  );
}

function ExpenseList({ items }) {
  return (
    <ul className="mt-4 space-y-2 pr-2">
      {items.map((t, i) => (
        <li key={i} className="flex gap-3 items-start text-stone-700 leading-[1.65]">
          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-rose-500 flex-shrink-0" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

function CtaButton({ href, onClick, testId }) {
  return (
    <div className="mt-6 text-center">
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        onClick={onClick}
        data-testid={testId}
        className="inline-flex items-center justify-center gap-2 h-14 px-6 md:px-8 rounded-full bg-brand-amber hover:bg-brand-amberHover text-white font-[800] transition active:scale-95 pulse-cta text-base md:text-lg"
      >
        {PRIMARY_CTA}
        <ArrowLeft className="w-5 h-5 flex-shrink-0" />
      </a>
    </div>
  );
}

function WhatsappCard({ text, testId, name = "ל***ה מ." }) {
  return (
    <div
      className="bg-gradient-to-tr from-emerald-50 to-white border border-emerald-200/60 rounded-2xl p-4"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
          <MessageCircle className="w-4 h-4 text-white" strokeWidth={2.5} />
        </div>
        <div className="flex-1">
          <div className="text-xs font-bold text-emerald-900">{name}</div>
          <div className="text-[10px] text-stone-500">10:24 ✓✓</div>
        </div>
      </div>
      <div className="bg-white rounded-2xl rounded-tr-none px-4 py-3 border border-emerald-100 text-stone-700 leading-[1.55] text-sm">
        {text}
      </div>
    </div>
  );
}

function ShortsCard({ videoId, testId }) {
  return (
    <div
      data-testid={testId}
      className="relative rounded-2xl overflow-hidden border border-black/5 bg-stone-100 aspect-[9/16] shadow-[0_8px_28px_rgba(28,25,23,0.10)]"
    >
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1`}
        title={`עדות וידאו ${videoId}`}
        className="absolute inset-0 w-full h-full"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

const SHORTS_TESTIMONIALS = [
  "RRiVV6PZxVs",
  "xhDUAkYIQmA",
  "gucvcPy5Ok8",
];

const FAQ_ITEMS = [
  {
    q: "רגע, אם אתם כל כך טובים, למה שיחת האסטרטגיה היא בחינם?",
    a: 'שאלה מעולה, ואני אענה עליה הכי ישיר שיש: אנחנו לא פילנתרופים. אנחנו עושים את זה כי מתוך המשפחות שאנחנו בודקים בחינם ומראים להם שאפשר לחסוך להם 3,000 ש״ח בחודש — חלק גדול מהן יבחרו לשכור את שירותי הליווי שלנו בתשלום כדי שאנחנו נרוץ לבנקים, נריב איתם, ונעשה להם את כל הביורוקרטיה השחורה במקומם. אבל הבדיקה עצמה? היא בחינם, כי אם אנחנו לא יכולים לעזור לך — אני לא אקח ממך שקל.',
  },
  {
    q: "זה יפגע לי במשכנתה הקיימת? התנאים שלי שם טובים!",
    a: 'ממש לא. ב-90% מהמקרים אנחנו בכלל לא נוגעים במשכנתה הישנה והטובה שלכם (במיוחד אם לקחתם אותה לפני 2021). אנחנו רק "מלבישים" עליה תוספת ייעודית בתנאים של משכנתה במקום בתנאים של "הלוואת זבל מסחרית", כדי לחסל את החובות הקצרים.',
  },
  {
    q: "למה שאני לא אלך פשוט לבנק שלי לבקש את זה?",
    a: "אתם מוזמנים לנסות. הבנקאי שלכם הוא איש מכירות של הבנק, לא היועץ שלכם. הוא יציע לכם עוד \"הלוואת גישור\" או ינסה למחזר לכם את כל המשכנתה לתנאים גרועים יותר כדי שהבנק ירוויח עליכם פעמיים. אנחנו באים מבחוץ, בלי אינטרס לבנק ספציפי, ויודעים איפה ללחוץ.",
  },
];
